import React, { useState, useMemo } from 'react';
import MantleGrid from '../../components/MantleGrid';
import { cell, GRID } from '../../styles/gridStyles';

// =============================================================================
// Price Implosion — UI MOCKUP (dummy data, no backend wiring yet)
// The differentiator: aggregate the cost stages from every active module into
// a bottom-up break-even, then set the list price from a target margin.
// Once wired, each stage pulls live from its module (BOM, OPEX, Distribution).
// Stage amounts are edited in the shared MantleGrid (same feel as Opex entry).
// =============================================================================

// Cost build-up per SKU (R per case). `source` shows which module will feed
// the number once wired — for now the values are editable dummies.
const DUMMY_SKUS = {
  'FG-1000': {
    description: 'Body Lotion 500ml — Case of 12',
    listPrice: 486.0,
    stages: [
      { id: 's1', stage: 'Raw Materials & Packaging', source: 'Manufacturing · BOM', amount: 148.2, color: 'bg-sky-500' },
      { id: 's2', stage: 'Inbound Logistics', source: 'Purchase · Landed Cost', amount: 22.6, color: 'bg-teal-500' },
      { id: 's3', stage: 'Direct Labour & Machine', source: 'Manufacturing · Work Centers', amount: 54.1, color: 'bg-amber-500' },
      { id: 's4', stage: 'Factory Overhead Recovery', source: 'Opex · Site Drivers', amount: 38.7, color: 'bg-orange-500' },
      { id: 's5', stage: 'Warehousing & Distribution', source: 'Distribution · DC + Routes', amount: 41.3, color: 'bg-violet-500' },
      { id: 's6', stage: 'Head Office Recovery', source: 'Opex · Group Allocation', amount: 18.9, color: 'bg-slate-500' },
    ],
  },
  'FG-2000': {
    description: 'Hand Sanitiser Gel 250ml — Case of 24',
    listPrice: 398.0,
    stages: [
      { id: 's1', stage: 'Raw Materials & Packaging', source: 'Manufacturing · BOM', amount: 131.4, color: 'bg-sky-500' },
      { id: 's2', stage: 'Inbound Logistics', source: 'Purchase · Landed Cost', amount: 18.2, color: 'bg-teal-500' },
      { id: 's3', stage: 'Direct Labour & Machine', source: 'Manufacturing · Work Centers', amount: 43.5, color: 'bg-amber-500' },
      { id: 's4', stage: 'Factory Overhead Recovery', source: 'Opex · Site Drivers', amount: 31.2, color: 'bg-orange-500' },
      { id: 's5', stage: 'Warehousing & Distribution', source: 'Distribution · DC + Routes', amount: 35.8, color: 'bg-violet-500' },
      { id: 's6', stage: 'Head Office Recovery', source: 'Opex · Group Allocation', amount: 15.4, color: 'bg-slate-500' },
    ],
  },
  'FG-3000': {
    description: 'Dishwash Liquid 750ml — Case of 6',
    listPrice: 210.0,
    stages: [
      { id: 's1', stage: 'Raw Materials & Packaging', source: 'Manufacturing · BOM', amount: 72.8, color: 'bg-sky-500' },
      { id: 's2', stage: 'Inbound Logistics', source: 'Purchase · Landed Cost', amount: 9.6, color: 'bg-teal-500' },
      { id: 's3', stage: 'Direct Labour & Machine', source: 'Manufacturing · Work Centers', amount: 24.9, color: 'bg-amber-500' },
      { id: 's4', stage: 'Factory Overhead Recovery', source: 'Opex · Site Drivers', amount: 19.3, color: 'bg-orange-500' },
      { id: 's5', stage: 'Warehousing & Distribution', source: 'Distribution · DC + Routes', amount: 22.1, color: 'bg-violet-500' },
      { id: 's6', stage: 'Head Office Recovery', source: 'Opex · Group Allocation', amount: 9.8, color: 'bg-slate-500' },
    ],
  },
};

const fmtR = (n) => `R ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const inputClass =
  'w-24 text-right text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 ' +
  'text-slate-800 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400';

const selectClass =
  'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 ' +
  'text-slate-700 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-slate-400';

const PriceImplosion = () => {
  const [sku, setSku] = useState('');
  const [stages, setStages] = useState([]);
  const [listPrice, setListPrice] = useState(0);
  const [targetMargin, setTargetMargin] = useState(25);

  const handleSelect = (code) => {
    setSku(code);
    const d = DUMMY_SKUS[code];
    setStages(d ? d.stages.map((s) => ({ ...s })) : []);
    setListPrice(d ? d.listPrice : 0);
  };

  const totalCost = useMemo(() => stages.reduce((a, s) => a + s.amount, 0), [stages]);
  // Margin on selling price: price = cost / (1 - margin%).
  const targetPrice = targetMargin < 100 ? totalCost / (1 - targetMargin / 100) : 0;
  const currentMargin = listPrice > 0 ? ((listPrice - totalCost) / listPrice) * 100 : 0;
  const priceGap = listPrice - targetPrice;

  // ---- Stage grid (MantleGrid) ---------------------------------------------
  const columns = useMemo(() => ([
    { columnId: 'stage', width: 240, resizable: true },
    { columnId: 'source', width: 230, resizable: true },
    { columnId: 'amount', width: 120, resizable: true },
    { columnId: 'pct', width: 100, resizable: true },
  ]), []);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header('Cost Stage'),
        cell.header('Feeds From (once wired)'),
        cell.header('R / Case', { align: 'right' }),
        cell.header('% of Cost', { align: 'right' }),
      ],
    };

    const dataRows = stages.map((s, idx) => ({
      rowId: s.id,
      height: GRID.size.dataRow,
      cells: [
        cell.label(s.stage, { idx }),
        cell.text(s.source, { idx }),
        cell.number(s.amount, { editable: true, idx }),
        cell.aggregate(totalCost > 0 ? Math.round((s.amount / totalCost) * 1000) / 10 : 0, { kind: 'quarter' }),
      ],
    }));

    const totalRow = {
      rowId: 'totals',
      height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('BREAK-EVEN / CASE'),
        cell.totalBlank(),
        cell.totalNumber(totalCost),
        cell.totalNumber(100),
      ],
    };

    return [headerRow, ...dataRows, totalRow];
  }, [stages, totalCost]);

  const handleChanges = (changes) => {
    setStages((prev) => {
      const next = prev.map((s) => ({ ...s }));
      for (const ch of changes) {
        if (String(ch.columnId) !== 'amount') continue;
        const val = Number(ch.newCell?.value);
        if (isNaN(val)) continue;
        const stage = next.find((s) => s.id === ch.rowId);
        if (stage) stage.amount = val;
      }
      return next;
    });
  };

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Price Implosion</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Every cost stage rolled into a bottom-up break-even, then a list price from your target margin.
          <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
            Mockup · dummy data
          </span>
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">SKU (Finished Good)</span>
            <select className={selectClass} value={sku} onChange={(e) => handleSelect(e.target.value)}>
              <option value="">Select a SKU…</option>
              {Object.entries(DUMMY_SKUS).map(([code, d]) => (
                <option key={code} value={code}>{code} · {d.description}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto">
        {!sku ? (
          <div className="h-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center">
            <p className="text-sm text-slate-400">Select a SKU to implode its costs into a price.</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 items-start">

            {/* Cost build-up */}
            <div className="flex-1 min-w-0 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                <span className="text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">Cost Build-Up (per case)</span>
              </div>

              {/* Stacked bar */}
              <div className="px-4 py-4">
                <div className="h-6 w-full flex rounded-md overflow-hidden border border-slate-200 dark:border-slate-700">
                  {stages.map((s) => (
                    <div
                      key={s.id}
                      className={`${s.color} h-full`}
                      style={{ width: `${totalCost > 0 ? (s.amount / totalCost) * 100 : 0}%` }}
                      title={`${s.stage}: ${fmtR(s.amount)}`}
                    />
                  ))}
                </div>
              </div>

              {/* Stage grid — fixed height: header + 6 stages + total row */}
              <div style={{ height: GRID.size.headerRow + stages.length * GRID.size.dataRow + GRID.size.totalRow + 40 }}>
                <MantleGrid
                  rows={gridRows}
                  columns={columns}
                  onCellsChanged={handleChanges}
                  hint="Amounts are editable dummies — once wired, each stage pulls live from its module."
                />
              </div>
            </div>

            {/* Pricing panel */}
            <div className="w-full lg:w-96 shrink-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
                <span className="text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">Pricing</span>
              </div>

              <div className="p-4 space-y-4">
                <label className="flex items-center justify-between gap-3 text-sm text-slate-600 dark:text-slate-300">
                  Target margin (on price)
                  <span className="flex items-center gap-1">
                    <input type="number" step="0.5" min="0" max="95" className={inputClass} value={targetMargin}
                      onChange={(e) => setTargetMargin(Number(e.target.value) || 0)} />
                    <span className="text-xs text-slate-400">%</span>
                  </span>
                </label>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/60">
                  <div className="px-3 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Break-even / case</span>
                    <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100">{fmtR(totalCost)}</span>
                  </div>
                  <div className="px-3 py-2.5 flex items-center justify-between bg-slate-50 dark:bg-slate-800/60">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Target list price</span>
                    <span className="font-mono text-base font-bold text-emerald-600 dark:text-emerald-400">{fmtR(targetPrice)}</span>
                  </div>
                </div>

                <label className="flex items-center justify-between gap-3 text-sm text-slate-600 dark:text-slate-300">
                  Current list price
                  <span className="flex items-center gap-1">
                    <span className="text-xs text-slate-400">R</span>
                    <input type="number" step="0.5" min="0" className={inputClass} value={listPrice}
                      onChange={(e) => setListPrice(Number(e.target.value) || 0)} />
                  </span>
                </label>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/60">
                  <div className="px-3 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Margin at current price</span>
                    <span className={`font-mono text-sm font-bold ${currentMargin >= targetMargin ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                      {currentMargin.toFixed(1)}%
                    </span>
                  </div>
                  <div className="px-3 py-2.5 flex items-center justify-between">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Current vs target price</span>
                    <span className={`font-mono text-sm font-bold ${priceGap >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                      {priceGap >= 0 ? '▲' : '▼'} {fmtR(Math.abs(priceGap))}
                    </span>
                  </div>
                </div>

                <p className="m-0 text-xs text-slate-400">
                  {currentMargin >= targetMargin
                    ? 'Current price covers the full cost stack at your target margin.'
                    : 'Current price is below target — either raise price, or attack a cost stage on the left.'}
                </p>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default PriceImplosion;
